import React, { ReactElement } from 'react';
import { motion, Variants } from 'framer-motion';
import styled from 'styled-components';

const LAYER_DURATION = 1.44;
const LAYER_STAGGER = 0.14;
const LAYER_COUNT = 4;

// Hold until the step's content has nearly finished leaving, so the gradient reads on its own.
const LAYER_LEAD_IN = 0.5;

export const EXIT_TRANSITION_DURATION_MS = (LAYER_LEAD_IN + (LAYER_COUNT - 1) * LAYER_STAGGER + LAYER_DURATION) * 1000;

// Layers leave down and to the left, where the container clips them. They also grow from their
// bottom-left corner by more than they travel, so the top and right edges never come into view.
const layerVariants: Variants = {
  rest: { x: 0, y: 0, scale: 1, opacity: 1 },
  exit: (index: number) => ({
    x: '-15%',
    y: '32%',
    scale: 1.35,
    opacity: 0,
    transition: {
      duration: LAYER_DURATION,
      ease: [0.4, 0, 0.2, 1],
      delay: LAYER_LEAD_IN + (LAYER_COUNT - 1 - index) * LAYER_STAGGER,
    },
  }),
};

// Four bands exported from the design system gradient, stacked. Figma applies the 0.7 opacity
// once across the whole stack, so it belongs on the container rather than each layer.
const Container = styled.div`
  bottom: 0px;
  display: none;
  height: 46vh;
  left: 0px;
  opacity: 0.7;
  overflow: hidden;
  pointer-events: none;
  position: fixed;
  right: 0px;
  z-index: 0;

  @media (min-width: ${(props) => props.theme.breakpoints.Mobile}) {
    display: block;
  }
`;

// Wider than the container so the artwork's dense corner sits past the right edge, which keeps
// the purple from crowding the page. The layers are pre-rendered: the Figma export's blur filters
// cover regions of up to 9900 x 17800 units, which Safari tries to allocate in full and chokes on.
const Layer = styled(motion.img)`
  height: 100%;
  left: 0px;
  object-fit: cover;
  object-position: right bottom;
  position: absolute;
  top: 0px;
  transform-origin: 0% 100%;
  width: 112%;
  will-change: transform, opacity;
`;

const TopFade = styled.div`
  background: linear-gradient(to bottom, #FFFFFF, rgba(255, 255, 255, 0) 55%);
  inset: 0px;
  position: absolute;
`;

type OnboardingGradientProps = {
  isExiting: boolean;
};

function OnboardingGradient({ isExiting }: OnboardingGradientProps): ReactElement {
  return (
    <Container aria-hidden>
      {Array.from({ length: LAYER_COUNT }, (unused, index) => (
        <Layer
          key={index}
          alt=""
          src={`/assets/onboarding-gradient-layer-${index + 1}.webp`}
          custom={index}
          initial="rest"
          animate={isExiting ? 'exit' : 'rest'}
          variants={layerVariants}
        />
      ))}

      <TopFade />
    </Container>
  );
}

export default OnboardingGradient;
